import { Injectable } from "@angular/core";
import {
  AngularFirestore,
  AngularFirestoreCollection,
  DocumentReference,
} from "@angular/fire/firestore";
import { firestore } from "firebase/app";
import { Observable } from "rxjs";
import { map, take } from "rxjs/operators";
import { AuthService } from "../auth/auth.service";
import { Installation } from "../../interfaces/installation";
import { SipServerConfig } from "src/app/interfaces/support";

@Injectable({
  providedIn: "root",
})
export class InstallationsService {
  private installationsCollection: AngularFirestoreCollection<Installation>;
  private installations: Observable<Installation[]>;
  public installationsData: Installation[];

  constructor(private afs: AngularFirestore, private authServ: AuthService) {
    this.installationsCollection = this.afs.collection<Installation>(
      "installations",
      (ref) => {
        return ref.orderBy("name", "asc");
      }
    );
    this.installations = this.installationsCollection.snapshotChanges().pipe(
      map((actions) => {
        return actions.map((a) => {
          const data = a.payload.doc.data() as Installation;
          const id = a.payload.doc.id;
          return { id, ...data };
        });
      })
    );
  }

  getInstallations(): Observable<Installation[]> {
    return this.installations;
  }

  getInstallation(id: string): Observable<Installation> {
    return this.installationsCollection
      .doc<Installation>(id)
      .valueChanges()
      .pipe(
        take(1),
        map((installation) => {
          installation.id = id;
          return installation;
        })
      );
  }

  addInstallation(installation: Installation): Promise<void> {
    installation.id = this.afs.createId();
    return this.installationsCollection.doc(installation.id).set(installation);
  }

  updateInstallation(installation: Installation): Promise<void> {
    return this.installationsCollection
      .doc(installation.id)
      .set(installation, { merge: true });
  }

  filterInstallations(filter: string): Observable<Installation[]> {
    filter = filter.toLowerCase();
    if (filter.length === 0) {
      return this.installations;
    }
    const strFrontCode = filter.slice(0, filter.length - 1);
    const strEndCode = filter.slice(filter.length - 1, filter.length);

    const startCode = filter;
    const endCode =
      strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1);

    // return this.installationsCollection.ref.where('name', '>=', startCode).where('name', '<', endCode).get();

    return this.installations.pipe(
      map((items) =>
        items.filter(
          (item) =>
            (item.name >= startCode && item.name <= endCode) ||
            item.name.includes(filter)
        )
      )
    );
  }

  // messages data

  setSipServers(installationId: string, sipServers: SipServerConfig[]) {
    sipServers.forEach((sipServer) => {
      if (!sipServer.created)
        sipServer.created = {
          user: {
            ref: this.authServ.userRef,
            name: this.authServ.user.displayName,
          },
          ts: firestore.Timestamp.fromDate(new Date()),
        };

      // Inicializa el array si no existe
      if (!Array.isArray(sipServer.updated)) {
        sipServer.updated = [];
      }

      // Agrega el nuevo registro al array
      sipServer.updated.push({
        user: {
          ref: this.authServ.userRef,
          name: this.authServ.user.displayName,
        },
        ts: firestore.Timestamp.fromDate(new Date()),
      });

      // Solo conserva los últimos 5 elementos
      if (sipServer.updated.length > 5) {
        sipServer.updated = sipServer.updated.slice(-5);
      }

      // array de extensions validas donde están solo las extensiones que no usen teléfono RingSimultaneously = "no"

      sipServer.extensionsData = sipServer.extensions
        .filter((ext) => ext.RingSimultaneously === "no")
        .map((ext) => ext.Extension);
      console.log(
        "🚀 ~ InstallationsService ~ sipServers.forEach ~ sipServer.extensionsData:",
        sipServer.extensionsData
      );

      this.afs
        .collection("messages")
        .doc(installationId)
        .collection("sip_servers")
        .doc(sipServer.id)
        .set(sipServer, {
          merge: true,
        });
    });
  }

  removeSipServer(installationId: string, sipServerId: string): Promise<void> {
    return this.afs
      .collection("messages")
      .doc(installationId)
      .collection("sip_servers")
      .doc(sipServerId)
      .delete();
  }

  loadSipServers(installationId: string): Observable<SipServerConfig[]> {
    return this.afs
      .collection("messages")
      .doc(installationId)
      .collection<SipServerConfig>("sip_servers")
      .valueChanges()
      .pipe(
        map((sipServers) => {
          return sipServers.map((sipServer) => {
            sipServer.id = sipServer.id;
            return sipServer;
          });
        })
      );
  }
}
